import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API } from '../App';
import { NavBar } from '../components/NavBar';
import { useSound } from '../hooks/useSound';
import { useLanguage } from '../contexts/LanguageContext';
import { CurriculumMapButton } from '../components/CurriculumMap';
import { BookOpen } from 'lucide-react';
import axios from 'axios';

export function MonthSelect() {
  const { grade } = useParams();
  const [months, setMonths] = useState([]);
  const navigate = useNavigate();
  const { playSound } = useSound();
  const { t } = useLanguage();

  useEffect(() => {
    axios.get(`${API}/grades/${grade}/months`).then(r => setMonths(r.data)).catch(console.error);
  }, [grade]);

  const handleMonthClick = (month) => {
    playSound('click');
    navigate(`/games?mode=school&grade=${grade}&month=${month}`);
  };

  const MONTH_COLORS = [
    '#FF6B35', '#E84855', '#BE3455', '#8B2FC9',
    '#2D82B7', '#4CAF50', '#66BB6A', '#81C784',
    '#FFB300', '#FF7043',
  ];

  return (
    <div data-testid="month-select-screen" style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>
      <NavBar
        items={[`${t('page.grade')} ${grade}`]}
        onBack={() => navigate('/browse', { replace: true })}
      />
      <div style={{ flex: 1, padding: '2rem', overflow: 'auto' }}>
        <h2 style={{
          fontFamily: 'Fredoka, sans-serif',
          fontSize: 'clamp(2rem, 4vw, 3rem)',
          fontWeight: 700,
          color: 'var(--text-primary)',
          marginBottom: '2rem',
          textAlign: 'center',
        }}>
          {t('page.selectMonth')}
        </h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: '1.25rem',
          maxWidth: 1400,
          margin: '0 auto',
        }}>
          {months.map((m, i) => (
            <div
              key={m.month}
              data-testid={`month-${m.month}-button`}
              role="button"
              tabIndex={0}
              onClick={() => handleMonthClick(m.month)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleMonthClick(m.month); }}
              style={{
                background: 'var(--surface)',
                border: '3px solid var(--border)',
                borderRadius: '1.25rem',
                padding: '1.5rem',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'all 0.2s',
                minHeight: 100,
                animation: `fade-in 0.4s ease ${i * 0.05}s both`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div style={{
                  width: 56,
                  height: 56,
                  borderRadius: '0.75rem',
                  background: MONTH_COLORS[i % 10],
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: 'Barlow Condensed, sans-serif',
                  fontSize: '1.75rem',
                  fontWeight: 800,
                  color: 'white',
                  flexShrink: 0,
                }}>
                  {(i + 1).toString().padStart(2, '0')}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: 'Fredoka, sans-serif', fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                    {m.name_fr}
                  </div>
                  <div style={{ fontSize: '1.1rem', color: 'var(--text-secondary)', marginTop: 2 }}>
                    {m.unit_title_fr}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 4 }}>
                    <span style={{ fontSize: '1rem', color: 'var(--text-muted)' }}>
                      {m.total_items} {t('page.items')} &middot; {m.game_count} {t('page.games')}
                    </span>
                  </div>
                </div>
              </div>
              <div style={{ marginTop: '0.75rem', display: 'flex', justifyContent: 'flex-end' }} onClick={e => e.stopPropagation()}>
                <CurriculumMapButton
                  size="small"
                  queryParams={{ mode: 'school', grade, month: m.month }}
                  title={`${t('page.grade')} ${grade} — ${m.name_fr}`}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
